"use client";

import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

export interface UserPlatformMeta {
    locale: {
        language: string | null;
        languages: string[];
        timezone: string | null;
    };
    ui: {
        prefersDarkMode: boolean;
        isMobile: boolean;
    };
    client: {
        userAgent: string | null;
        platform: string | null;
        hardwareConcurrency: number | null;
        deviceMemory: number | null;
        touchSupport: boolean;
        screen: {
            width: number | null;
            height: number | null;
            pixelRatio: number | null;
        };
    };
    network: {
        effectiveType?: string;
        saveData?: boolean;
        downlink?: number;
        rtt?: number;
    };
}

/**
 * Collects basic user/platform metadata for backend context.
 * Safe defaults when running in non-browser environments.
 */
export function useUserPlatformMeta(): UserPlatformMeta {
    const theme = useTheme();
    const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [viewport, setViewport] = useState<{ w: number | null; h: number | null }>({
        w: typeof window !== "undefined" ? window.innerWidth : null,
        h: typeof window !== "undefined" ? window.innerHeight : null,
    });

    useEffect(() => {
        if (typeof window === "undefined") return;
        const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const meta = useMemo<UserPlatformMeta>(() => {
        const nav = typeof navigator !== "undefined" ? navigator : ({} as Navigator);
        const lang = (nav as any).language ?? null;
        const langs = Array.isArray((nav as any).languages) ? (nav as any).languages as string[] : (lang ? [lang] : []);
        const tz = typeof Intl !== "undefined" && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone ?? null : null;

        const connection = (nav as any).connection || (nav as any).mozConnection || (nav as any).webkitConnection;

        return {
            locale: {
                language: lang,
                languages: langs,
                timezone: tz,
            },
            ui: {
                prefersDarkMode: !!prefersDark,
                isMobile: !!isMobile,
            },
            client: {
                userAgent: (nav as any).userAgent ?? null,
                platform: (nav as any).platform ?? null,
                hardwareConcurrency: typeof (nav as any).hardwareConcurrency === "number" ? (nav as any).hardwareConcurrency : null,
                deviceMemory: typeof (nav as any).deviceMemory === "number" ? (nav as any).deviceMemory : null,
                touchSupport: typeof window !== "undefined" ? ("ontouchstart" in window || (nav as any).maxTouchPoints > 0) : false,
                screen: {
                    width: typeof window !== "undefined" ? window.screen?.width ?? viewport.w : viewport.w,
                    height: typeof window !== "undefined" ? window.screen?.height ?? viewport.h : viewport.h,
                    pixelRatio: typeof window !== "undefined" ? window.devicePixelRatio ?? null : null,
                },
            },
            network: connection
                ? {
                    effectiveType: connection.effectiveType,
                    saveData: !!connection.saveData,
                    downlink: typeof connection.downlink === "number" ? connection.downlink : undefined,
                    rtt: typeof connection.rtt === "number" ? connection.rtt : undefined,
                }
                : {},
        };
    }, [isMobile, prefersDark, viewport.h, viewport.w]);

    return meta;
}

export default useUserPlatformMeta;

